import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import {
  FileText, Users, Clock, Calendar, CheckCircle2, ChevronDown, ChevronRight,
  Download, ArrowLeft, Loader2, User, Target, MessageSquare, RefreshCw, AlertCircle, Mail
} from 'lucide-react';
import axios from 'axios';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';

const API = process.env.REACT_APP_BACKEND_URL;

// ─── Agenda Section (Collapsible) ───
const AgendaSection = ({ item, index }) => {
  const [open, setOpen] = useState(true);
  return (
    <div className="border border-slate-700/50 rounded-xl overflow-hidden" data-testid={`agenda-section-${index}`}>
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-4 bg-slate-800/40 hover:bg-slate-800/60 transition-colors text-left">
        <div className="flex items-center gap-2">
          <span className="w-6 h-6 rounded-lg bg-emerald-500/20 text-emerald-400 text-xs font-bold flex items-center justify-center">{index + 1}</span>
          <span className="text-sm font-medium text-white">{item.topic}</span>
        </div>
        {open ? <ChevronDown className="h-4 w-4 text-slate-500" /> : <ChevronRight className="h-4 w-4 text-slate-500" />}
      </button>
      {open && (
        <div className="p-4 space-y-3 bg-slate-800/20">
          {item.discussion_summary && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 font-medium">Discussion Summary</p>
              <p className="text-xs text-slate-300 leading-relaxed">{item.discussion_summary}</p>
            </div>
          )}
          {item.key_points?.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 font-medium">Key Points</p>
              <ul className="space-y-1">
                {item.key_points.map((kp, i) => (
                  <li key={i} className="text-xs text-slate-300 flex items-start gap-1.5">
                    <span className="text-emerald-400 mt-0.5">•</span>{kp}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {item.decisions_made?.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 font-medium">Decisions Made</p>
              <ul className="space-y-1">
                {item.decisions_made.map((d, i) => (
                  <li key={i} className="text-xs text-emerald-300 flex items-start gap-1.5">
                    <CheckCircle2 className="h-3 w-3 mt-0.5 shrink-0" />{d}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {item.action_items?.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 font-medium">Action Items</p>
              <div className="space-y-1.5">
                {item.action_items.map((ai, i) => (
                  <div key={i} className="flex items-start gap-2 p-2 rounded-lg bg-slate-800/50 border border-slate-700/30">
                    <Target className="h-3.5 w-3.5 text-amber-400 mt-0.5 shrink-0" />
                    <div className="text-xs">
                      <p className="text-white">{ai.action}</p>
                      <p className="text-slate-500">{ai.person_in_charge}{ai.deadline ? ` · Due: ${ai.deadline}` : ''}</p>
                    </div>
                    <Badge className={cn("text-[9px] ml-auto shrink-0",
                      ai.status === 'done' ? "bg-emerald-500/20 text-emerald-400" : "bg-amber-500/20 text-amber-400"
                    )}>{ai.status || 'pending'}</Badge>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Main Minutes Page ───
export default function MeetingMinutesPage() {
  const { meetingId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const userId = user?.id || user?._id || '';

  const [meeting, setMeeting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acknowledging, setAcknowledging] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);
  const [retrying, setRetrying] = useState(false);

  const fetchMeeting = async () => {
    try {
      const res = await axios.get(`${API}/api/meetings/${meetingId}`);
      setMeeting(res.data);
    } catch {
      toast.error('Failed to load meeting');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchMeeting();
    // Poll if processing
    const poll = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/api/meetings/${meetingId}`);
        setMeeting(res.data);
        if (res.data.status !== 'processing') clearInterval(poll);
      } catch { /* */ }
    }, 5000);
    return () => clearInterval(poll);
  }, [meetingId]);

  const handleAcknowledge = async () => {
    setAcknowledging(true);
    try {
      await axios.post(`${API}/api/meetings/${meetingId}/acknowledge`, {});
      toast.success('Minutes acknowledged');
      const res = await axios.get(`${API}/api/meetings/${meetingId}`);
      setMeeting(res.data);
    } catch (err) {
      toast.error('Failed to acknowledge');
    }
    setAcknowledging(false);
  };

  const handleRetry = async () => {
    setRetrying(true);
    try {
      await axios.post(`${API}/api/meetings/${meetingId}/retry`, {});
      toast.success('AI pipeline restarted');
      fetchMeeting();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to retry');
    }
    setRetrying(false);
  };

  if (loading) {
    return (
      <div className="min-h-[80vh] -m-4 md:-m-6 lg:-m-8 flex items-center justify-center bg-slate-900">
        <Loader2 className="h-8 w-8 text-emerald-400 animate-spin" />
      </div>
    );
  }

  if (!meeting) {
    return (
      <div className="min-h-[80vh] -m-4 md:-m-6 lg:-m-8 flex items-center justify-center bg-slate-900">
        <p className="text-slate-400">Meeting not found</p>
      </div>
    );
  }

  // Processing state
  if (meeting.status === 'processing') {
    return (
      <div className="min-h-[80vh] -m-4 md:-m-6 lg:-m-8 flex items-center justify-center bg-slate-900" data-testid="minutes-processing">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 rounded-full bg-blue-500/20 flex items-center justify-center mx-auto mb-4">
            <Loader2 className="h-8 w-8 text-blue-400 animate-spin" />
          </div>
          <h2 className="text-lg font-bold text-white mb-1">Generating Meeting Minutes</h2>
          <p className="text-sm text-slate-400">AI is transcribing and analyzing your meeting...</p>
          <p className="text-xs text-slate-500 mt-4">{meeting.chunk_count || 0} audio chunks · {meeting.participants?.length || 0} participants</p>
        </div>
      </div>
    );
  }

  // Error state
  if (meeting.status === 'error') {
    const isHost = meeting.host_id === userId;
    return (
      <div className="min-h-[80vh] -m-4 md:-m-6 lg:-m-8 flex items-center justify-center bg-slate-900" data-testid="minutes-error">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="h-8 w-8 text-red-400" />
          </div>
          <h2 className="text-lg font-bold text-white mb-1">Processing Error</h2>
          <p className="text-sm text-slate-400 mb-4">Something went wrong while generating meeting minutes.</p>
          {meeting.error && <p className="text-xs text-red-400/70 mb-4 bg-red-500/10 rounded-lg p-2">{meeting.error}</p>}
          <div className="flex gap-2 justify-center">
            <Button variant="outline" className="border-slate-600 text-slate-300" onClick={() => navigate('/meeting')}>
              <ArrowLeft className="h-4 w-4 mr-1" />Back
            </Button>
            {isHost && (
              <Button className="bg-emerald-600 hover:bg-emerald-500 gap-1.5" onClick={handleRetry} disabled={retrying} data-testid="retry-pipeline-btn">
                {retrying ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                Retry AI Processing
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  const minutes = meeting.minutes || {};
  const acks = meeting.acknowledgments || [];
  const totalParticipants = meeting.participants?.length || 0;
  const hasAcknowledged = acks.some(a => a.user_id === userId);

  return (
    <div className="min-h-[80vh] -m-4 md:-m-6 lg:-m-8 p-4 md:p-6 bg-gradient-to-b from-slate-900 via-slate-900 to-slate-800" data-testid="meeting-minutes-page">
      <div className="max-w-3xl mx-auto">
        {/* Back Button */}
        <Button variant="ghost" className="text-slate-400 hover:text-white mb-4 gap-1.5 text-xs" onClick={() => navigate('/meeting')}>
          <ArrowLeft className="h-3.5 w-3.5" />All Meetings
        </Button>

        {/* Meeting Header */}
        <div className="mb-6">
          <h1 className="text-xl font-bold text-white flex items-center gap-2">
            <FileText className="h-5 w-5 text-emerald-400" />
            {minutes.meeting_title || meeting.title}<PageInfoTooltip pageKey="meeting-minutes" />
          </h1>
          <div className="flex items-center gap-3 mt-2 text-xs text-slate-400 flex-wrap">
            <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{new Date(minutes.date || meeting.created_at).toLocaleDateString()}</span>
            <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{minutes.duration || `${Math.floor((meeting.duration_seconds || 0) / 60)}m`}</span>
            <span className="flex items-center gap-1"><Users className="h-3 w-3" />{totalParticipants} participants</span>
            {meeting.emails_sent > 0 && (
              <span className="flex items-center gap-1 text-blue-400"><Mail className="h-3 w-3" />Emailed to {meeting.emails_sent} participant{meeting.emails_sent > 1 ? 's' : ''}</span>
            )}
          </div>
        </div>

        {/* Attendees */}
        <Card className="bg-slate-800/60 border-slate-700/50 mb-4">
          <CardContent className="p-3">
            <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-2 font-medium">Attendees</p>
            <div className="flex flex-wrap gap-1.5">
              {(minutes.attendees || meeting.participants || []).map((a, i) => (
                <div key={i} className="flex items-center gap-1.5 bg-slate-700/50 px-2 py-1 rounded-lg">
                  <User className="h-3 w-3 text-slate-500" />
                  <span className="text-xs text-slate-300">{a.name}</span>
                  {a.role === 'host' && <Badge className="text-[8px] bg-emerald-500/20 text-emerald-400">Host</Badge>}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Overall Summary */}
        {minutes.overall_summary && (
          <Card className="bg-emerald-500/5 border-emerald-500/20 mb-4">
            <CardContent className="p-4">
              <p className="text-[10px] uppercase tracking-wider text-emerald-500/60 mb-1.5 font-medium">Overall Summary</p>
              <p className="text-sm text-slate-300 leading-relaxed">{minutes.overall_summary}</p>
            </CardContent>
          </Card>
        )}

        {/* Agenda Sections */}
        <div className="space-y-3 mb-6">
          {(minutes.agenda_items || []).map((item, i) => (
            <AgendaSection key={i} item={item} index={i} />
          ))}
        </div>

        {/* Download PDF + Acknowledgment */}
        <Card className="bg-slate-800/60 border-slate-700/50 mb-4">
          <CardContent className="p-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <p className="text-sm font-medium text-white">Meeting Minutes</p>
                <p className="text-[10px] text-slate-500">
                  Reviewed by {acks.length}/{totalParticipants} participants
                </p>
                {acks.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {acks.map((a, i) => (
                      <span key={i} className="text-[9px] bg-emerald-500/10 text-emerald-400 px-1.5 py-0.5 rounded">{a.name}</span>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                {meeting.pdf_path && (
                  <Button variant="outline" size="sm" className="border-slate-600 text-slate-300 text-xs gap-1.5" data-testid="download-pdf-btn"
                    onClick={() => window.open(`${API}/api/meetings/${meetingId}/pdf`, '_blank')}>
                    <Download className="h-3.5 w-3.5" />Download PDF
                  </Button>
                )}
                {!hasAcknowledged ? (
                  <Button className="bg-emerald-600 hover:bg-emerald-500 text-xs gap-1.5" onClick={handleAcknowledge} disabled={acknowledging} data-testid="acknowledge-btn">
                    {acknowledging ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                    Acknowledge
                  </Button>
                ) : (
                  <Badge className="bg-emerald-500/20 text-emerald-400 text-xs gap-1"><CheckCircle2 className="h-3 w-3" />Acknowledged</Badge>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Transcript Toggle */}
        {meeting.transcript && (
          <div className="mb-4">
            <Button variant="ghost" className="text-slate-400 text-xs gap-1.5" onClick={() => setShowTranscript(!showTranscript)}>
              <MessageSquare className="h-3.5 w-3.5" />
              {showTranscript ? 'Hide' : 'Show'} Full Transcript
            </Button>
            {showTranscript && (
              <Card className="bg-slate-800/60 border-slate-700/50 mt-2">
                <CardContent className="p-4 max-h-[400px] overflow-y-auto">
                  <pre className="text-xs text-slate-400 whitespace-pre-wrap font-sans leading-relaxed">{
                    typeof meeting.transcript === 'string' ? meeting.transcript :
                    JSON.stringify(meeting.transcript, null, 2)
                  }</pre>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Info */}
        <p className="text-center text-[10px] text-slate-600 mt-4">
          {minutes.total_chunks ? `${minutes.total_chunks} audio chunks recorded` : ''}
          {minutes.audio_merged ? ' · Audio merged successfully' : ''}
          {minutes.transcription_available === false ? ' · Transcription unavailable' : ''}
        </p>

        {/* Retry button for host when transcription wasn't available */}
        {meeting.host_id === userId && minutes.transcription_available === false && (
          <div className="text-center mt-3">
            <Button variant="outline" size="sm" className="border-slate-600 text-slate-400 text-xs gap-1.5" onClick={handleRetry} disabled={retrying} data-testid="retry-transcription-btn">
              {retrying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
              Retry AI Transcription
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
